import {
  type InstitutionDetailsFuturesKey,
  type InstitutionDetailsOnboardingKey,
  type InstitutionDetailsPartial,
  institutionDetailsFuturesSchema,
  institutionDetailsOnboardingSchema,
} from '@/services/api/profile';

export const INSTITUTION_DETAILS_TYPE = {
  ONBOARDING: 'onboarding',
  FUTURES: 'futures',
} as const;

export type InstitutionDetailsType = (typeof INSTITUTION_DETAILS_TYPE)[keyof typeof INSTITUTION_DETAILS_TYPE];

export type InstitutionDetailsKeyForDetailsType<T extends InstitutionDetailsType> = T extends typeof INSTITUTION_DETAILS_TYPE.ONBOARDING
  ? InstitutionDetailsOnboardingKey
  : T extends typeof INSTITUTION_DETAILS_TYPE.FUTURES
    ? InstitutionDetailsFuturesKey
    : never;

const ONBOARDING_DETAIL_KEYS = Object.keys(institutionDetailsOnboardingSchema.shape) as InstitutionDetailsOnboardingKey[];

const ONBOARDING_DETAILS_KEY_ROUTE_MAP = {
  incorporation: 'incorporation',
  branchOperations: 'branch-operations',
  company: 'company',
  regulatoryInformation: 'regulatory-information',
  authorisation: 'authorisation',
} as const satisfies Record<InstitutionDetailsOnboardingKey, string>;

const FUTURES_DETAIL_KEYS = Object.keys(institutionDetailsFuturesSchema.shape) as InstitutionDetailsFuturesKey[];

const FUTURES_DETAILS_KEY_ROUTE_MAP = {
  decisionMaker: 'decision-maker',
  execution: 'execution',
  clientCategorisation: 'client-categorisation',
  reporting: 'reporting',
} as const satisfies Record<InstitutionDetailsFuturesKey, string>;

const DETAILS_TYPE_KEYS_MAP = {
  [INSTITUTION_DETAILS_TYPE.ONBOARDING]: ONBOARDING_DETAIL_KEYS,
  [INSTITUTION_DETAILS_TYPE.FUTURES]: FUTURES_DETAIL_KEYS,
};

type DetailsTypeKeysMap = typeof DETAILS_TYPE_KEYS_MAP;

const DETAILS_TYPE_ROUTES_MAP = {
  [INSTITUTION_DETAILS_TYPE.ONBOARDING]: ONBOARDING_DETAILS_KEY_ROUTE_MAP,
  [INSTITUTION_DETAILS_TYPE.FUTURES]: FUTURES_DETAILS_KEY_ROUTE_MAP,
};

const DETAILS_TYPE_SCHEMA_MAP = {
  [INSTITUTION_DETAILS_TYPE.ONBOARDING]: institutionDetailsOnboardingSchema,
  [INSTITUTION_DETAILS_TYPE.FUTURES]: institutionDetailsFuturesSchema,
};

export const getDetailsKeyIndex = <T extends InstitutionDetailsType>(detailType: T, key: DetailsTypeKeysMap[T][number]): number => {
  const detailKeys = DETAILS_TYPE_KEYS_MAP[detailType] as ReadonlyArray<DetailsTypeKeysMap[T][number]>;
  const keyIndex = detailKeys.indexOf(key);
  if (keyIndex === -1) throw new Error(`Key ${key} not found for ${detailType}`);

  return keyIndex;
};

export const getNextDetailsKey = async <T extends InstitutionDetailsType>(
  detailType: T,
  institutionDetails: InstitutionDetailsPartial | null | undefined,
): Promise<InstitutionDetailsKeyForDetailsType<T> | undefined> => {
  const detailKeys = DETAILS_TYPE_KEYS_MAP[detailType];
  const schema = DETAILS_TYPE_SCHEMA_MAP[detailType];
  const result = await schema.safeParseAsync(institutionDetails);
  if (result.success) return undefined;

  const firstError = result.error.issues[0];
  const nextDetailsKey = firstError?.path[0]?.toString();
  if (!nextDetailsKey) return detailKeys[0] as InstitutionDetailsKeyForDetailsType<T>;

  return nextDetailsKey as InstitutionDetailsKeyForDetailsType<T>;
};

export const getNextDetailsKeyRoute = async <T extends InstitutionDetailsType>(
  detailType: T,
  institutionDetails: InstitutionDetailsPartial | null | undefined,
): Promise<string | undefined> => {
  const nextDetailsKey = await getNextDetailsKey(detailType, institutionDetails);
  if (!nextDetailsKey) return undefined;

  return (DETAILS_TYPE_ROUTES_MAP[detailType] as Record<InstitutionDetailsKeyForDetailsType<T>, string>)[nextDetailsKey];
};

export const getDetailsKeyProgress = <T extends InstitutionDetailsType>(detailType: T, key: DetailsTypeKeysMap[T][number], currentStep?: number, totalSteps?: number) => {
  const detailKeys = DETAILS_TYPE_KEYS_MAP[detailType];
  const keyIndex = getDetailsKeyIndex(detailType, key);
  const stepProgress = totalSteps && currentStep ? currentStep / totalSteps : 0;
  const progress = (keyIndex + stepProgress) / detailKeys.length;

  return Number(Math.min(progress, (keyIndex + 1) / detailKeys.length).toFixed(2));
};

const ALGORITHM_ID_LENGTH = 30;

export const generateAlgorithmId = (companyName: string) => {
  const prefix = companyName
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 8);
  const rest = crypto
    .randomUUID()
    .replace(/-/g, '')
    .toUpperCase()
    .slice(0, ALGORITHM_ID_LENGTH - prefix.length);

  return `${prefix}${rest}`;
};
